import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  TextField,
  useTheme,
  IconButton,
  Tooltip,
  Paper,
  Collapse,
  Chip,
  Stack,
} from '@mui/material'
import { alpha, type Theme } from '@mui/material/styles'
import { deepOrange } from '@mui/material/colors'
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Timeline as TimelineIcon,
  ContactPhone as ContactPhoneIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Flag as FlagIcon,
  Photo as PhotoIcon,
  Videocam as VideocamIcon,
  PictureAsPdf as PictureAsPdfIcon,
  OpenInNew as OpenInNewIcon,
  CompareArrows as CompareArrowsIcon,
} from '@mui/icons-material'
import { getStorage, ref, getDownloadURL } from '@firebase/storage'
import dayjs from 'dayjs'
import { canonicalVehicleTier } from '@otw/models/inventory'
import { getRepairTruckWorkStatus } from '@otw/models/logistics'
import type { RepairTruck, RepairTruckWorkNote, RepairTruckWorkStatus } from '@otw/models/logistics'
import { countDamagePhotos } from '@otw/models/repair'
import type { TransportTruckWithVehicle } from '@/hooks/logistics/useLogistics'
import { getFundedStatusDisplay } from '../../utils/fundedStatusDisplay'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import TimelineDialog from '../dialogs/TimelineDialog'
import InspectionChecklistAnswersDialog, {
  resolveCompleteChecklistId,
  resolveIntakeChecklistId,
} from '../dialogs/InspectionChecklistAnswersDialog'
import MediaGalleryDialog, { type MediaGalleryItem } from '../dialogs/MediaGalleryDialog'
import { usePickupLocations } from '@/hooks/inventory/usePickupLocations'
import { useRepairShopLocations } from '@/hooks/inventory/useRepairShopLocations'
import { useInspectionChecklist } from '@/hooks/repair/useInspectionChecklist'
import { useRepairUsers } from '@/hooks/admin/useRepairUsers'
import { RepairInvoiceExpenseActions } from '../RepairInvoiceExpenseActions'
import {
  RepairEstimatePdfCompareDialog,
  type RepairEstimatePdfCompareItem,
} from '../RepairEstimatePdfCompareDialog'

const WORK_STATUS_OPTIONS: RepairTruckWorkStatus[] = [
  'Awaiting estimate',
  'in progress',
  'complete',
]

function formatNoteTime(timestamp: { toMillis?: () => number }): string {
  const ms = timestamp?.toMillis?.() ?? 0
  return dayjs(ms).format('MMM D, h:mm A')
}

function normalizeTimelineText(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase()
}

type TimelineEntryType =
  | 'note'
  | 'status'
  | 'paid'
  | 'pickup_ready'
  | 'being_painted'
  | 'media'
  | 'media-photo'
  | 'media-damage'
  | 'media-video'
  | 'media-invoice'
  | 'media-estimate'

const REPAIR_TIMELINE_KIND_LABEL: Record<TimelineEntryType, string> = {
  note: 'Note',
  status: 'Status update',
  paid: 'Payment update',
  pickup_ready: 'Ready for pickup',
  being_painted: 'Paint status',
  media: 'Media',
  'media-photo': 'Photo',
  'media-damage': 'Damage photo',
  'media-video': 'Video',
  'media-invoice': 'Invoice',
  'media-estimate': 'Estimate',
}

type PendingConfirmAction =
  | { type: 'work_status'; nextStatus: RepairTruckWorkStatus }
  | { type: 'paid'; nextValue: boolean }
  | { type: 'being_painted'; nextValue: boolean }

function getTimelineEntryType(entry: RepairTruckWorkNote): TimelineEntryType {
  const mediaType = entry.media?.type
  if (mediaType === 'photo') return 'media-photo'
  if (mediaType === 'damage_photo') return 'media-damage'
  if (mediaType === 'video') return 'media-video'
  if (mediaType === 'invoice') return 'media-invoice'
  if (mediaType === 'estimate') return 'media-estimate'
  const text = (entry.note ?? '').trim().toLowerCase()
  if (text.startsWith('work status updated to')) return 'status'
  if (text.startsWith('work status changed:')) return 'status'
  if (text === 'payment completed' || text === 'payment marked as pending') return 'paid'
  if (text.startsWith('paid status changed:')) return 'paid'
  if (text.includes('being painted') || text.startsWith('paint status')) return 'being_painted'
  if (text === 'truck marked ready for pickup. repairs complete.') return 'pickup_ready'
  return 'note'
}

/** Matches RepairDashboard.tsx repair timeline card styling */
function repairTimelineCardSx(theme: Theme, kind: TimelineEntryType) {
  const base = {
    flex: '0 0 auto' as const,
    minWidth: 220,
    maxWidth: 360,
    p: 1.25,
    borderRadius: 1.5,
    border: '1px solid',
    borderLeftWidth: 4,
    borderLeftStyle: 'solid' as const,
  }
  switch (kind) {
    case 'note':
      return {
        ...base,
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.primary.main, 0.12)
            : alpha(theme.palette.primary.main, 0.06),
        borderColor: alpha(theme.palette.primary.main, 0.22),
        borderLeftColor: theme.palette.primary.main,
      }
    case 'status':
      return {
        ...base,
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.info.main, 0.14)
            : alpha(theme.palette.info.main, 0.08),
        borderColor: alpha(theme.palette.info.main, 0.22),
        borderLeftColor: theme.palette.info.main,
      }
    case 'paid':
      return {
        ...base,
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.success.main, 0.14)
            : alpha(theme.palette.success.main, 0.08),
        borderColor: alpha(theme.palette.success.main, 0.22),
        borderLeftColor: theme.palette.success.main,
      }
    case 'pickup_ready':
      return {
        ...base,
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.warning.main, 0.18)
            : alpha(theme.palette.warning.main, 0.1),
        borderColor: alpha(theme.palette.warning.main, 0.28),
        borderLeftColor: theme.palette.warning.main,
      }
    case 'being_painted':
      return {
        ...base,
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(deepOrange[400], 0.16)
            : alpha(deepOrange[500], 0.08),
        borderColor: alpha(deepOrange[500], 0.28),
        borderLeftColor: deepOrange[500],
      }
    case 'media':
      return {
        ...base,
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.info.main, 0.16)
            : alpha(theme.palette.info.main, 0.08),
        borderColor: alpha(theme.palette.info.main, 0.24),
        borderLeftColor: theme.palette.info.main,
      }
    case 'media-photo':
      return {
        ...base,
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.success.main, 0.12)
            : alpha(theme.palette.success.main, 0.07),
        borderColor: alpha(theme.palette.success.main, 0.22),
        borderLeftColor: theme.palette.success.main,
      }
    case 'media-damage':
      return {
        ...base,
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.error.main, 0.16)
            : alpha(theme.palette.error.main, 0.08),
        borderColor: alpha(theme.palette.error.main, 0.25),
        borderLeftColor: theme.palette.error.main,
      }
    case 'media-video':
      return {
        ...base,
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.secondary.main, 0.14)
            : alpha(theme.palette.secondary.main, 0.08),
        borderColor: alpha(theme.palette.secondary.main, 0.22),
        borderLeftColor: theme.palette.secondary.main,
      }
    case 'media-invoice':
      return {
        ...base,
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.warning.main, 0.16)
            : alpha(theme.palette.warning.main, 0.08),
        borderColor: alpha(theme.palette.warning.main, 0.25),
        borderLeftColor: theme.palette.warning.main,
      }
    case 'media-estimate':
      return {
        ...base,
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.warning.dark, 0.22)
            : alpha(theme.palette.warning.main, 0.12),
        borderColor: alpha(theme.palette.warning.main, 0.3),
        borderLeftColor: theme.palette.warning.dark,
      }
    default:
      return {
        ...base,
        bgcolor: 'background.paper',
        borderColor: 'divider',
        borderLeftColor: 'divider',
      }
  }
}

function repairTimelineKindLabelColor(theme: Theme, kind: TimelineEntryType): string {
  switch (kind) {
    case 'note':
      return 'primary.main'
    case 'status':
      return 'info.main'
    case 'paid':
      return 'success.main'
    case 'pickup_ready':
      return 'warning.main'
    case 'being_painted':
      return theme.palette.mode === 'dark' ? deepOrange[300] : deepOrange[800]
    case 'media':
      return 'info.main'
    case 'media-photo':
      return 'success.main'
    case 'media-damage':
      return 'error.main'
    case 'media-video':
      return 'secondary.main'
    case 'media-invoice':
      return 'warning.main'
    case 'media-estimate':
      return 'warning.dark'
    default:
      return 'text.secondary'
  }
}

function useWorkNoteMediaDownloadUrl(storagePath: string | undefined) {
  const [url, setUrl] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')

  useEffect(() => {
    if (!storagePath) {
      setUrl(null)
      setStatus('idle')
      return
    }
    let cancelled = false
    setStatus('loading')
    const storage = getStorage()
    getDownloadURL(ref(storage, storagePath))
      .then((u) => {
        if (!cancelled) {
          setUrl(u)
          setStatus('ready')
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUrl(null)
          setStatus('error')
        }
      })
    return () => {
      cancelled = true
    }
  }, [storagePath])

  return { url, status }
}

function RepairWorkNotePdfLink({
  storagePath,
  mediaKind,
  hasOptionalNote,
}: {
  storagePath: string
  mediaKind: 'invoice' | 'estimate'
  hasOptionalNote: boolean
}) {
  const { url, status } = useWorkNoteMediaDownloadUrl(storagePath)

  if (status === 'loading') {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: hasOptionalNote ? 0.75 : 0 }}
      >
        Loading media…
      </Typography>
    )
  }
  if (status === 'error' || !url) {
    return (
      <Typography
        variant="caption"
        color="error"
        sx={{ display: 'block', mb: hasOptionalNote ? 0.75 : 0 }}
      >
        Could not open media
      </Typography>
    )
  }

  return (
    <Button
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      variant="outlined"
      size="small"
      startIcon={<PictureAsPdfIcon fontSize="small" />}
      endIcon={<OpenInNewIcon fontSize="small" />}
      sx={{ textTransform: 'none', mb: hasOptionalNote ? 0.75 : 0 }}
    >
      {mediaKind === 'estimate' ? 'Open estimate' : 'Open invoice'}
    </Button>
  )
}

export interface RepairTruckWithTruck {
  repairTruck: RepairTruck
  truckWithVehicle: TransportTruckWithVehicle
}

/** Capitalize first letter of a string (e.g. "in progress" -> "In progress"). */
function capitalizeFirst(s: string): string {
  if (!s) return s
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export interface RepairTrackingViewProps {
  items: RepairTruckWithTruck[]
  getRepairShopLabel: (item: RepairTruckWithTruck) => string
  /** Resolve user_id to display name for note author. */
  getUserName?: (userId: string) => string
  onAddNote: (
    repairTruckId: string,
    payload: { note: string; visible_to_repair_users?: boolean }
  ) => Promise<void>
  onEditNote: (repairTruckId: string, noteIndex: number, note: string) => Promise<void>
  onSetNoteVisibleToRepairUsers: (
    repairTruckId: string,
    noteIndex: number,
    visible_to_repair_users: boolean
  ) => Promise<void>
  onDeleteNote: (repairTruckId: string, noteIndex: number) => Promise<void>
  onUpdateWorkStatus: (repairTruckId: string, work_status: RepairTruckWorkStatus) => Promise<void>
  onReviewEstimate: (
    repairTruckId: string,
    noteIndex: number,
    status: 'approved' | 'rejected'
  ) => Promise<void>
  onUpdatePaid: (repairTruckId: string, paid: boolean) => Promise<void>
  onUpdateBeingPainted: (repairTruckId: string, being_painted: boolean) => Promise<void>
  onToggleFlag: (repairTruckId: string, flagged: boolean) => Promise<void>
  isAddingNote?: boolean
  isEditingNote?: boolean
  isDeletingNote?: boolean
  isUpdatingWorkStatus?: boolean
  isReviewingEstimate?: boolean
  isUpdatingPaid?: boolean
  isUpdatingBeingPainted?: boolean
  isUpdatingFlag?: boolean
}

interface ContactInfo {
  name: string
  phone: string
  email?: string
  address: string
}

function formatLocationAddress(location?: {
  address?: string
  city?: string
  state?: string
  zip?: string
}): string {
  if (!location) return '—'
  const line1 = location.address?.trim() ?? ''
  const line2 = [location.city, location.state, location.zip]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(', ')
  if (line1 && line2) return `${line1}, ${line2}`
  return line1 || line2 || '—'
}

type IndexedTimelineNote = {
  entry: RepairTruckWorkNote
  /** Index in Firestore `work_notes` when this row is a stored note; synthetic rows use null */
  workNotesIndex: number | null
  visualIdx: number
}

type MediaTimelineEntry = {
  entry: RepairTruckWorkNote
  visualIdx: number
  workNotesIndex: number | null
}

function buildMediaGalleryItems(
  entries: MediaTimelineEntry[],
  mediaKind: 'photo' | 'video' | 'damage_photo',
  getUserName?: (userId: string) => string
): MediaGalleryItem[] {
  return entries
    .filter(
      ({ entry }) => entry.media?.type === mediaKind && Boolean(entry.media?.storage_path)
    )
    .map(({ entry, visualIdx }) => {
      const storagePath = entry.media?.storage_path || ''
      const userText =
        entry.user_id && getUserName
          ? (getUserName(entry.user_id) ?? 'Unknown')
          : (entry.user_name || entry.user_id || 'Unknown')
      return {
        key: `${mediaKind}-${visualIdx}`,
        storagePath,
        mediaKind: mediaKind === 'damage_photo' ? 'photo' : mediaKind,
        noteText: (entry.note ?? '').trim(),
        metaText: `${userText} · ${formatNoteTime(entry.timestamp)}`,
      }
    })
}

type TimelineDisplayItem =
  | { kind: 'entry'; visualIdx: number; workNotesIndex: number | null; entry: RepairTruckWorkNote }
  | {
      kind: 'media-group'
      entries: MediaTimelineEntry[]
      photoCount: number
      videoCount: number
    }
  | { kind: 'damage-group'; entries: MediaTimelineEntry[]; damageCount: number }

function isPhotoOrVideoTimelineType(type: TimelineEntryType): boolean {
  return type === 'media-photo' || type === 'media-video'
}

function buildTimelineDisplayItems(notes: IndexedTimelineNote[]): TimelineDisplayItem[] {
  const items: TimelineDisplayItem[] = []
  let i = 0

  while (i < notes.length) {
    const current = notes[i]
    const currentType = getTimelineEntryType(current.entry)
    const isCurrentPhotoVideo = isPhotoOrVideoTimelineType(currentType)
    const isCurrentDamage = currentType === 'media-damage'

    if (!isCurrentPhotoVideo && !isCurrentDamage) {
      items.push({
        kind: 'entry',
        visualIdx: current.visualIdx,
        workNotesIndex: current.workNotesIndex,
        entry: current.entry,
      })
      i += 1
      continue
    }

    if (isCurrentPhotoVideo) {
      const mediaRun: MediaTimelineEntry[] = []
      while (i < notes.length) {
        const candidate = notes[i]
        const candidateType = getTimelineEntryType(candidate.entry)
        if (!isPhotoOrVideoTimelineType(candidateType)) break
        mediaRun.push({
          entry: candidate.entry,
          visualIdx: candidate.visualIdx,
          workNotesIndex: candidate.workNotesIndex,
        })
        i += 1
      }

      if (mediaRun.length >= 2) {
        let photoCount = 0
        let videoCount = 0
        mediaRun.forEach(({ entry }) => {
          if (entry.media?.type === 'photo') photoCount += 1
          if (entry.media?.type === 'video') videoCount += 1
        })
        items.push({ kind: 'media-group', entries: mediaRun, photoCount, videoCount })
      } else if (mediaRun.length === 1) {
        const only = mediaRun[0]
        items.push({
          kind: 'entry',
          visualIdx: only.visualIdx,
          workNotesIndex: only.workNotesIndex,
          entry: only.entry,
        })
      }
      continue
    }

    const damageRun: MediaTimelineEntry[] = []
    while (i < notes.length) {
      const candidate = notes[i]
      const candidateType = getTimelineEntryType(candidate.entry)
      if (candidateType !== 'media-damage') break
      damageRun.push({
        entry: candidate.entry,
        visualIdx: candidate.visualIdx,
        workNotesIndex: candidate.workNotesIndex,
      })
      i += 1
    }

    if (damageRun.length >= 2) {
      items.push({ kind: 'damage-group', entries: damageRun, damageCount: damageRun.length })
    } else if (damageRun.length === 1) {
      const only = damageRun[0]
      items.push({
        kind: 'entry',
        visualIdx: only.visualIdx,
        workNotesIndex: only.workNotesIndex,
        entry: only.entry,
      })
    }
  }

  return items
}

function RepairTruckTrackingCard({
  item,
  getRepairShopLabel,
  getUserName,
  customerContact,
  repairShopContact,
  onAddNote,
  onEditNote,
  onSetNoteVisibleToRepairUsers,
  onDeleteNote,
  onUpdateWorkStatus,
  onReviewEstimate,
  onUpdatePaid,
  onUpdateBeingPainted,
  onToggleFlag,
  isAddingNote,
  isEditingNote,
  isDeletingNote,
  isUpdatingWorkStatus,
  isReviewingEstimate,
  isUpdatingPaid,
  isUpdatingBeingPainted,
  isUpdatingFlag,
  repairAuthorUserIds,
  completeChecklistId,
  intakeChecklistId,
}: {
  item: RepairTruckWithTruck
  getRepairShopLabel: (item: RepairTruckWithTruck) => string
  getUserName?: (userId: string) => string
  customerContact: ContactInfo | null
  repairShopContact: ContactInfo | null
  onAddNote: (
    repairTruckId: string,
    payload: { note: string; visible_to_repair_users?: boolean }
  ) => Promise<void>
  onEditNote: (repairTruckId: string, noteIndex: number, note: string) => Promise<void>
  onSetNoteVisibleToRepairUsers: (
    repairTruckId: string,
    noteIndex: number,
    visible_to_repair_users: boolean
  ) => Promise<void>
  onDeleteNote: (repairTruckId: string, noteIndex: number) => Promise<void>
  onUpdateWorkStatus: (repairTruckId: string, work_status: RepairTruckWorkStatus) => Promise<void>
  onReviewEstimate: (
    repairTruckId: string,
    noteIndex: number,
    status: 'approved' | 'rejected'
  ) => Promise<void>
  onUpdatePaid: (repairTruckId: string, paid: boolean) => Promise<void>
  onUpdateBeingPainted: (repairTruckId: string, being_painted: boolean) => Promise<void>
  onToggleFlag: (repairTruckId: string, flagged: boolean) => Promise<void>
  isAddingNote?: boolean
  isEditingNote?: boolean
  isDeletingNote?: boolean
  isUpdatingWorkStatus?: boolean
  isReviewingEstimate?: boolean
  isUpdatingPaid?: boolean
  isUpdatingBeingPainted?: boolean
  isUpdatingFlag?: boolean
  repairAuthorUserIds: Set<string>
  /** Firestore complete checklist template id for this truck's repair shop */
  completeChecklistId: string
  /** Firestore intake checklist template id for this truck's repair shop */
  intakeChecklistId: string
}) {
  const theme = useTheme()
  const { questions: checklistQuestions } = useInspectionChecklist({
    checklistId: completeChecklistId,
    kind: 'complete',
    fallbackToDefault: true,
  })
  const { repairTruck, truckWithVehicle } = item
  const vehicle = truckWithVehicle.vehicle
  const vehicleLabel = vehicle
    ? `${vehicle.year ?? ''} ${vehicle.make ?? ''} ${vehicle.model ?? ''}`.trim() || '—'
    : '—'
  const priorityRank =
    typeof repairTruck.priority_rank === 'number' && Number.isFinite(repairTruck.priority_rank)
      ? repairTruck.priority_rank
      : null
  const tierLabel = canonicalVehicleTier(vehicle?.tier) || '—'
  const shopLabel = getRepairShopLabel(item)
  const fundedStatusDisplay = getFundedStatusDisplay(vehicle)

  const [noteInput, setNoteInput] = useState('')
  const [visibleToRepairUsers, setVisibleToRepairUsers] = useState(false)
  const [showAddNote, setShowAddNote] = useState(false)
  const [addingForId, setAddingForId] = useState<string | null>(null)
  const [editingNoteIndex, setEditingNoteIndex] = useState<number | null>(null)
  const [editingNoteInput, setEditingNoteInput] = useState('')
  const [editingForId, setEditingForId] = useState<string | null>(null)
  const [deletingForId, setDeletingForId] = useState<string | null>(null)
  const [deleteConfirmWorkNotesIndex, setDeleteConfirmWorkNotesIndex] = useState<number | null>(null)
  const [visibilityToggleWorkNotesIndex, setVisibilityToggleWorkNotesIndex] = useState<number | null>(
    null
  )
  const [pendingConfirmAction, setPendingConfirmAction] = useState<PendingConfirmAction | null>(null)
  const [pendingFlagToggle, setPendingFlagToggle] = useState<boolean | null>(null)
  const [isTimelineDialogOpen, setIsTimelineDialogOpen] = useState(false)
  const [estimatePdfCompare, setEstimatePdfCompare] = useState<{
    older: RepairEstimatePdfCompareItem
    newer: RepairEstimatePdfCompareItem
  } | null>(null)
  const [isContactInfoOpen, setIsContactInfoOpen] = useState(false)
  const [isChecklistDialogOpen, setIsChecklistDialogOpen] = useState(false)
  const [openMediaGroupEntries, setOpenMediaGroupEntries] = useState<MediaTimelineEntry[] | null>(null)
  const checklistResponses = repairTruck.inspection_checklist?.responses ?? {}
  const enabledChecklistQuestions = checklistQuestions.filter((question) => question.enabled !== false)
  const totalChecklistQuestions = enabledChecklistQuestions.length
  const answeredChecklistQuestions = enabledChecklistQuestions.filter((question) => {
    const answer = checklistResponses[question.id]?.answer
    return answer === 'yes' || answer === 'no' || answer === 'na'
  }).length
  const isChecklistComplete =
    totalChecklistQuestions > 0 && answeredChecklistQuestions >= totalChecklistQuestions

  const handleAddNote = async () => {
    const trimmed = noteInput.trim()
    if (!trimmed) return
    setAddingForId(repairTruck.id)
    try {
      await onAddNote(repairTruck.id, {
        note: trimmed,
        visible_to_repair_users: visibleToRepairUsers,
      })
      setNoteInput('')
      setVisibleToRepairUsers(false)
      setShowAddNote(false)
    } finally {
      setAddingForId(null)
    }
  }

  const handleCancelAddNote = () => {
    setShowAddNote(false)
    setNoteInput('')
    setVisibleToRepairUsers(false)
  }

  const handleStartEditNote = (noteIndex: number, currentNote: string) => {
    setEditingNoteIndex(noteIndex)
    setEditingNoteInput(currentNote)
  }

  const handleCancelEditNote = () => {
    setEditingNoteIndex(null)
    setEditingNoteInput('')
  }

  const handleSaveEditNote = async () => {
    if (editingNoteIndex == null) return
    const trimmed = editingNoteInput.trim()
    if (!trimmed) return
    setEditingForId(repairTruck.id)
    try {
      await onEditNote(repairTruck.id, editingNoteIndex, trimmed)
      setEditingNoteIndex(null)
      setEditingNoteInput('')
    } finally {
      setEditingForId(null)
    }
  }

  const handleDeleteNote = async (workNotesIndex: number) => {
    setDeletingForId(repairTruck.id)
    try {
      await onDeleteNote(repairTruck.id, workNotesIndex)
      if (editingNoteIndex === workNotesIndex) {
        setEditingNoteIndex(null)
        setEditingNoteInput('')
      }
      setDeleteConfirmWorkNotesIndex(null)
    } finally {
      setDeletingForId(null)
    }
  }

  const handleToggleNoteVisibleToRepairUsers = async (workNotesIndex: number, next: boolean) => {
    setVisibilityToggleWorkNotesIndex(workNotesIndex)
    try {
      await onSetNoteVisibleToRepairUsers(repairTruck.id, workNotesIndex, next)
    } finally {
      setVisibilityToggleWorkNotesIndex(null)
    }
  }

  const notes = repairTruck.work_notes ?? []
  const indexedTimelineNotes = useMemo<IndexedTimelineNote[]>(() => {
    let visualIdx = 0
    const merged: IndexedTimelineNote[] = notes.map((entry, workNotesIndex) => ({
      entry,
      workNotesIndex,
      visualIdx: visualIdx++,
    }))
    const existingNoteTexts = new Set(merged.map((row) => normalizeTimelineText(row.entry.note)))

    ;(repairTruck.work_status_history ?? []).forEach((statusEntry) => {
      const status = (statusEntry?.status ?? '').trim()
      const timestamp = statusEntry?.timestamp
      if (!status || !timestamp) return
      const statusNote = `Work status updated to ${status}`
      const normalizedStatusNote = normalizeTimelineText(statusNote)
      if (existingNoteTexts.has(normalizedStatusNote)) return
      merged.push({
        entry: {
          note: statusNote,
          timestamp,
          user_id: statusEntry.user_id ?? '',
        },
        workNotesIndex: null,
        visualIdx: visualIdx++,
      })
      existingNoteTexts.add(normalizedStatusNote)
    })

    return merged.sort((left, right) => {
      const leftMs = left.entry.timestamp?.toMillis?.() ?? 0
      const rightMs = right.entry.timestamp?.toMillis?.() ?? 0
      return leftMs - rightMs
    })
  }, [notes, repairTruck.work_status_history])
  const timelineDisplayItems = useMemo(
    () => buildTimelineDisplayItems(indexedTimelineNotes),
    [indexedTimelineNotes]
  )
  const photoDialogItems = useMemo(
    () => buildMediaGalleryItems(openMediaGroupEntries ?? [], 'photo', getUserName),
    [openMediaGroupEntries, getUserName]
  )
  const damagePhotoDialogItems = useMemo(
    () => buildMediaGalleryItems(openMediaGroupEntries ?? [], 'damage_photo', getUserName),
    [openMediaGroupEntries, getUserName]
  )
  const videoDialogItems = useMemo(
    () => buildMediaGalleryItems(openMediaGroupEntries ?? [], 'video', getUserName),
    [openMediaGroupEntries, getUserName]
  )
  const openMediaGallery = (entries: MediaTimelineEntry[]) => {
    setOpenMediaGroupEntries(entries)
  }
  const currentWorkStatus = getRepairTruckWorkStatus(repairTruck)
  const damagePhotoCount = countDamagePhotos(repairTruck)
  const estimateNotes = useMemo(
    () =>
      (repairTruck.work_notes ?? [])
        .filter((entry) => entry.media?.type === 'estimate' && entry.media?.storage_path)
        .sort((left, right) => {
          const leftMs = left.timestamp?.toMillis?.() ?? 0
          const rightMs = right.timestamp?.toMillis?.() ?? 0
          return leftMs - rightMs
        }),
    [repairTruck.work_notes]
  )

  const openEstimateCompare = () => {
    if (estimateNotes.length < 2) return
    const olderNote = estimateNotes[estimateNotes.length - 2]
    const newerNote = estimateNotes[estimateNotes.length - 1]
    const toItem = (
      note: (typeof estimateNotes)[number],
      index: number
    ): RepairEstimatePdfCompareItem => ({
      label: `Estimate ${index + 1}`,
      storagePath: note.media!.storage_path!,
      uploadedAt: note.timestamp ? formatNoteTime(note.timestamp) : undefined,
      note: note.note?.trim() || undefined,
    })

    setEstimatePdfCompare({
      older: toItem(olderNote, estimateNotes.length - 2),
      newer: toItem(newerNote, estimateNotes.length - 1),
    })
  }

  const handleConfirmAction = async () => {
    if (!pendingConfirmAction) return

    try {
      if (pendingConfirmAction.type === 'work_status') {
        await onUpdateWorkStatus(repairTruck.id, pendingConfirmAction.nextStatus)
      } else if (pendingConfirmAction.type === 'paid') {
        await onUpdatePaid(repairTruck.id, pendingConfirmAction.nextValue)
      } else {
        await onUpdateBeingPainted(repairTruck.id, pendingConfirmAction.nextValue)
      }
    } finally {
      setPendingConfirmAction(null)
    }
  }

  const handleCancelActionConfirm = () => {
    setPendingConfirmAction(null)
  }

  const actionConfirmDialog = (() => {
    if (!pendingConfirmAction) return null

    if (pendingConfirmAction.type === 'work_status') {
      return {
        title: 'Change work status?',
        message: `Set work status to "${capitalizeFirst(pendingConfirmAction.nextStatus)}"?`,
        confirmText: 'Change status',
        severity: 'warning' as const,
      }
    }

    if (pendingConfirmAction.type === 'paid') {
      return {
        title: pendingConfirmAction.nextValue ? 'Mark as paid?' : 'Mark as unpaid?',
        message: pendingConfirmAction.nextValue
          ? 'This will mark this repair as paid.'
          : 'This will mark this repair as unpaid.',
        confirmText: pendingConfirmAction.nextValue ? 'Mark paid' : 'Mark unpaid',
        severity: 'warning' as const,
      }
    }

    return {
      title: pendingConfirmAction.nextValue ? 'Mark as being painted?' : 'Mark as not being painted?',
      message: pendingConfirmAction.nextValue
        ? 'This will mark the truck as being painted and update the vehicle color.'
        : 'This will mark the truck as not being painted.',
      confirmText: pendingConfirmAction.nextValue ? 'Mark being painted' : 'Mark not being painted',
      severity: 'warning' as const,
    }
  })()

  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 2,
        borderColor: 'divider',
      }}
    >
      {/* Header: vehicle + meta on left, controls on right */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'grey.50',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1.5 }}>
          <Tooltip title={repairTruck.flagged ? 'Remove flag' : 'Flag truck'}>
            <span>
              <IconButton
                size="small"
                onClick={() => setPendingFlagToggle(!repairTruck.flagged)}
                disabled={!!isUpdatingFlag}
                aria-label={repairTruck.flagged ? 'Remove flag' : 'Flag truck'}
                sx={{ p: 0.25 }}
              >
                <FlagIcon
                  fontSize="small"
                  sx={{ color: repairTruck.flagged ? 'warning.main' : 'action.disabled' }}
                />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="h6" fontWeight={600} component="span">
            {vehicleLabel}
          </Typography>
          <Chip
            size="small"
            label={priorityRank != null ? `#${priorityRank}` : '—'}
            color={priorityRank != null ? 'primary' : 'default'}
            variant={priorityRank != null ? 'filled' : 'outlined'}
            sx={{ height: 24 }}
          />
          {vehicle?.year != null ? (
            <Typography variant="body2" color="text.primary" component="span">
              {vehicle.year}
            </Typography>
          ) : null}
          <Typography variant="body2" color="text.primary" component="span">
            Stock #{vehicle?.stock_number ?? '—'}
          </Typography>
          <Typography variant="body2" color="text.primary" component="span">
            ·
          </Typography>
          <Typography variant="body2" color="text.primary" component="span">
            {tierLabel}
          </Typography>
          <Typography variant="body2" color="text.primary" component="span">
            ·
          </Typography>
          <Typography variant="body2" color="text.primary" component="span">
            VIN {truckWithVehicle.truck.vin ?? '—'}
          </Typography>
          <Typography variant="body2" color="text.primary" component="span">
            ·
          </Typography>
          <Typography variant="body2" color="text.primary" component="span">
            {shopLabel}
          </Typography>
          <Chip
            size="small"
            label={fundedStatusDisplay.label}
            color={fundedStatusDisplay.chipColor}
            variant={
              fundedStatusDisplay.chipColor === 'error' ||
              fundedStatusDisplay.chipColor === 'default'
                ? 'outlined'
                : 'filled'
            }
            sx={{ height: 24 }}
          />
          {damagePhotoCount > 0 ? (
            <>
              <Typography variant="body2" color="text.primary" component="span">
                ·
              </Typography>
              <Chip
                size="small"
                icon={<PhotoIcon sx={{ fontSize: 14 }} />}
                label={`${damagePhotoCount} damage photo${damagePhotoCount === 1 ? '' : 's'}`}
                color="error"
                variant="outlined"
                sx={{ height: 24 }}
              />
            </>
          ) : null}

        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            size="small"
            variant={isChecklistDialogOpen ? 'contained' : 'outlined'}
            color={isChecklistComplete ? 'success' : 'inherit'}
            startIcon={<AssignmentIcon fontSize="small" />}
            endIcon={isChecklistComplete ? <CheckCircleIcon fontSize="small" /> : undefined}
            onClick={() => setIsChecklistDialogOpen(true)}
            sx={{ textTransform: 'none', minWidth: 200 }}
          >
            Checklist answers ({answeredChecklistQuestions}/{totalChecklistQuestions})
          </Button>
          {estimateNotes.length >= 2 ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<CompareArrowsIcon fontSize="small" />}
              onClick={openEstimateCompare}
              sx={{ textTransform: 'none', minWidth: 200 }}
            >
              Compare estimates
            </Button>
          ) : null}
        <Button
            size="small"
            variant={isContactInfoOpen ? 'contained' : 'outlined'}
            color="inherit"
            startIcon={<ContactPhoneIcon fontSize="small" />}
            endIcon={isContactInfoOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            onClick={() => setIsContactInfoOpen((prev) => !prev)}
            sx={{ textTransform: 'none', minWidth: 120}}
          >
            Contacts
          </Button>
          <Tooltip title="View timeline">
            <span>
              <IconButton
                size="small"
                onClick={() => setIsTimelineDialogOpen(true)}
                aria-label="View timeline"
                disabled={!vehicle}
              >
                <TimelineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 150 }} disabled={!!isUpdatingWorkStatus}>
            <InputLabel>Work status</InputLabel>
            <Select
              label="Work status"
              value={currentWorkStatus}
              onChange={(e) => {
                const nextStatus = e.target.value as RepairTruckWorkStatus
                if (nextStatus === currentWorkStatus) return
                setPendingConfirmAction({ type: 'work_status', nextStatus })
              }}
            >
              {WORK_STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {capitalizeFirst(s)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={repairTruck.paid}
                onChange={(e) =>
                  setPendingConfirmAction({ type: 'paid', nextValue: e.target.checked })
                }
                disabled={!!isUpdatingPaid}
              />
            }
            label="Paid"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(repairTruck.being_painted)}
                onChange={(e) =>
                  setPendingConfirmAction({ type: 'being_painted', nextValue: e.target.checked })
                }
                disabled={!!isUpdatingBeingPainted}
              />
            }
            label="Being painted"
          />
        </Box>
      </Box>

      <CardContent sx={{ py: 2, px: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Collapse in={isContactInfoOpen} timeout="auto" unmountOnExit>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              borderColor: 'divider',
              backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : 'grey.50',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
              <ContactPhoneIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Contact info
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 1.25,
              }}
            >
              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                <Typography
                  variant="caption"
                  color="text.primary"
                  fontWeight={600}
                  sx={{ textTransform: 'uppercase' }}
                >
                  Customer
                </Typography>
                {customerContact ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {customerContact.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {customerContact.phone}
                    </Typography>
                    {customerContact.email && (
                      <Typography variant="body2" color="text.secondary">
                        {customerContact.email}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {customerContact.address}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    No final pickup location contact found.
                  </Typography>
                )}
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                <Typography
                  variant="caption"
                  color="text.primary"
                  fontWeight={600}
                  sx={{ textTransform: 'uppercase' }}
                >
                  Repair shop
                </Typography>
                {repairShopContact ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {repairShopContact.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {repairShopContact.phone}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {repairShopContact.address}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    No repair shop contact found.
                  </Typography>
                )}
              </Paper>
            </Box>
          </Paper>
        </Collapse>

        {/* Notes timeline */}
        {indexedTimelineNotes.length === 0 && !showAddNote ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No notes yet.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1.5,
              overflowX: 'auto',
              pb: 0.5,
              minHeight: 56,
            }}
          >
            {timelineDisplayItems.map((item) => {
              if (item.kind === 'damage-group') {
                const firstIdx = item.entries[0]?.visualIdx ?? 0
                const lastIdx = item.entries[item.entries.length - 1]?.visualIdx ?? firstIdx
                return (
                  <Box
                    key={`damage-group-${firstIdx}-${lastIdx}`}
                    sx={repairTimelineCardSx(theme, 'media-damage')}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 0.75,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: 0.65,
                          color: repairTimelineKindLabelColor(theme, 'media-damage'),
                        }}
                      >
                        Damage photos
                      </Typography>
                      <Chip
                        size="small"
                        icon={<PhotoIcon sx={{ fontSize: 14 }} />}
                        label={item.damageCount}
                        color="error"
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {item.entries.length} damage photo{item.entries.length === 1 ? '' : 's'}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => openMediaGallery(item.entries)}
                      sx={{ textTransform: 'none' }}
                    >
                      View damage photos
                    </Button>
                  </Box>
                )
              }

              if (item.kind === 'media-group') {
                const firstIdx = item.entries[0]?.visualIdx ?? 0
                const lastIdx = item.entries[item.entries.length - 1]?.visualIdx ?? firstIdx
                const title =
                  item.photoCount > 0 && item.videoCount > 0
                    ? 'Media'
                    : item.photoCount > 0
                      ? 'Photos'
                      : 'Videos'
                const kind: TimelineEntryType =
                  item.photoCount > 0 && item.videoCount > 0
                    ? 'media'
                    : item.photoCount > 0
                      ? 'media-photo'
                      : 'media-video'
                return (
                  <Box key={`media-group-${firstIdx}-${lastIdx}`} sx={repairTimelineCardSx(theme, kind)}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 0.75,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: 0.65,
                          color: repairTimelineKindLabelColor(theme, kind),
                        }}
                      >
                        {title}
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        <Chip
                          size="small"
                          icon={<PhotoIcon sx={{ fontSize: 14 }} />}
                          label={item.photoCount}
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          icon={<VideocamIcon sx={{ fontSize: 14 }} />}
                          label={item.videoCount}
                          variant="outlined"
                        />
                      </Stack>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {item.entries.length} media items
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openMediaGallery(item.entries)}
                      sx={{ textTransform: 'none' }}
                    >
                      View media
                    </Button>
                  </Box>
                )
              }

              const n = item.entry
              const visualIdx = item.visualIdx
              const workNotesIndex = item.workNotesIndex
              const entryType = getTimelineEntryType(n)
              const isEditableNote = entryType === 'note' && workNotesIndex != null
              const isRepairTimelineAuthor =
                Boolean(n.user_id) && repairAuthorUserIds.has(n.user_id ?? '')
              const showRepairVisibilityToggle = isEditableNote && !isRepairTimelineAuthor
              const isEditingThisNote =
                workNotesIndex != null && editingNoteIndex === workNotesIndex
              const noteText = (n.note ?? '').trim()
              const mediaPath = n.media?.storage_path
              const mediaKind = n.media?.type
              const noteMutationBusy =
                !!isEditingNote ||
                !!isDeletingNote ||
                editingForId === repairTruck.id ||
                deletingForId === repairTruck.id ||
                visibilityToggleWorkNotesIndex !== null

              return (
                <Box key={`tl-${visualIdx}`} sx={repairTimelineCardSx(theme, entryType)}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.65,
                      mb: 0.75,
                      color: repairTimelineKindLabelColor(theme, entryType),
                    }}
                  >
                    {REPAIR_TIMELINE_KIND_LABEL[entryType]}
                  </Typography>
                  {mediaPath && mediaKind === 'photo' ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PhotoIcon fontSize="small" />}
                      onClick={() =>
                        openMediaGallery([
                          { entry: n, visualIdx, workNotesIndex },
                        ])
                      }
                      sx={{ textTransform: 'none', mb: noteText ? 0.75 : 0 }}
                    >
                      View photo
                    </Button>
                  ) : null}
                  {mediaPath && mediaKind === 'damage_photo' ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<PhotoIcon fontSize="small" />}
                      onClick={() =>
                        openMediaGallery([
                          { entry: n, visualIdx, workNotesIndex },
                        ])
                      }
                      sx={{ textTransform: 'none', mb: noteText ? 0.75 : 0 }}
                    >
                      View damage photo
                    </Button>
                  ) : null}
                  {mediaPath && mediaKind === 'video' ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VideocamIcon fontSize="small" />}
                      onClick={() =>
                        openMediaGallery([
                          { entry: n, visualIdx, workNotesIndex },
                        ])
                      }
                      sx={{ textTransform: 'none', mb: noteText ? 0.75 : 0 }}
                    >
                      View video
                    </Button>
                  ) : null}
                  {mediaPath && mediaKind === 'invoice' ? (
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                      alignItems="center"
                      sx={{ mb: noteText ? 0.75 : 0 }}
                    >
                      <RepairWorkNotePdfLink
                        storagePath={mediaPath}
                        mediaKind="invoice"
                        hasOptionalNote={false}
                      />
                      <RepairInvoiceExpenseActions
                        storagePath={mediaPath}
                        repairTruckId={repairTruck.id}
                        repairShopId={repairTruck.repair_shop_location_id}
                        vehicleId={vehicle?.id ?? null}
                        note={noteText || undefined}
                      />
                    </Stack>
                  ) : null}
                  {mediaPath && mediaKind === 'estimate' ? (
                    <Box sx={{ mb: noteText ? 0.75 : 0 }}>
                      <RepairWorkNotePdfLink
                        storagePath={mediaPath}
                        mediaKind="estimate"
                        hasOptionalNote={!!noteText}
                      />
                      {workNotesIndex != null ? (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mt: 0.75 }}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          {n.estimate_review?.status === 'approved' && (
                            <Chip size="small" color="success" label="Estimate approved" />
                          )}
                          {n.estimate_review?.status === 'rejected' && (
                            <Chip size="small" color="error" label="Estimate rejected" />
                          )}
                          {n.estimate_review?.status !== 'approved' &&
                            n.estimate_review?.status !== 'rejected' && (
                              <>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  disabled={!!isReviewingEstimate}
                                  onClick={() => {
                                    void onReviewEstimate(repairTruck.id, workNotesIndex, 'approved')
                                  }}
                                >
                                  Approve estimate
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  disabled={!!isReviewingEstimate}
                                  onClick={() => {
                                    void onReviewEstimate(repairTruck.id, workNotesIndex, 'rejected')
                                  }}
                                >
                                  Reject estimate
                                </Button>
                              </>
                            )}
                        </Stack>
                      ) : null}
                    </Box>
                  ) : null}
                  {isEditingThisNote ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <TextField
                        size="small"
                        value={editingNoteInput}
                        onChange={(e) => setEditingNoteInput(e.target.value)}
                        multiline
                        minRows={2}
                        maxRows={6}
                        autoFocus
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={handleCancelEditNote}
                          disabled={!!isEditingNote || editingForId === repairTruck.id}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleSaveEditNote}
                          disabled={
                            !editingNoteInput.trim() || !!isEditingNote || editingForId === repairTruck.id
                          }
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : noteText ? (
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {n.note}
                    </Typography>
                  ) : !mediaPath ? (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  ) : null}
                  <Box
                    sx={{
                      mt: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block">
                      {n.user_id && getUserName
                        ? `${getUserName(n.user_id) ?? 'Unknown'} · ${formatNoteTime(n.timestamp)}`
                        : formatNoteTime(n.timestamp)}
                    </Typography>
                    {isEditableNote && !isEditingThisNote && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <Tooltip title="Edit note">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (workNotesIndex == null) return
                                handleStartEditNote(workNotesIndex, n.note ?? '')
                              }}
                              disabled={noteMutationBusy}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete note">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (workNotesIndex == null) return
                                setDeleteConfirmWorkNotesIndex(workNotesIndex)
                              }}
                              disabled={noteMutationBusy}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                  {showRepairVisibilityToggle && workNotesIndex != null ? (
                    <FormControlLabel
                      sx={{ display: 'flex', ml: -0.5, mt: 0.75, alignItems: 'center' }}
                      control={
                        <Checkbox
                          size="small"
                          checked={n.visible_to_repair_users === true}
                          disabled={noteMutationBusy}
                          onChange={(e) => {
                            void handleToggleNoteVisibleToRepairUsers(
                              workNotesIndex,
                              e.target.checked
                            )
                          }}
                        />
                      }
                      label={
                        <Typography variant="caption" color="text.secondary">
                          Visible to repair users
                        </Typography>
                      }
                    />
                  ) : null}
                </Box>
              )
            })}
            {showAddNote && (
              <Box
                sx={{
                  flex: '0 0 auto',
                  minWidth: 260,
                  maxWidth: 360,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'grey.50',
                  border: '1px dashed',
                  borderColor: 'primary.main',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
                >
                  New Note
                </Typography>
                <TextField
                  size="small"
                  placeholder="Type your note..."
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAddNote()}
                  multiline
                  minRows={3}
                  maxRows={6}
                  autoFocus
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={visibleToRepairUsers}
                      onChange={(e) => setVisibleToRepairUsers(e.target.checked)}
                    />
                  }
                  label="Visible to repair users"
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleCancelAddNote}
                    disabled={isAddingNote || addingForId === repairTruck.id}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleAddNote}
                    disabled={!noteInput.trim() || isAddingNote || addingForId === repairTruck.id}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Add note button: centered at bottom, circle + label */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setShowAddNote(true)}
            disabled={showAddNote}
            startIcon={
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: 'currentColor',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AddIcon sx={{ fontSize: 18 }} />
              </Box>
            }
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Add note
          </Button>
        </Box>
      </CardContent>
      <ConfirmDialog
        open={deleteConfirmWorkNotesIndex != null}
        title="Delete note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        severity="error"
        onCancel={() => setDeleteConfirmWorkNotesIndex(null)}
        onConfirm={() => {
          if (deleteConfirmWorkNotesIndex == null) return
          handleDeleteNote(deleteConfirmWorkNotesIndex)
        }}
      />
      {actionConfirmDialog && (
        <ConfirmDialog
          open={pendingConfirmAction != null}
          title={actionConfirmDialog.title}
          message={actionConfirmDialog.message}
          confirmText={actionConfirmDialog.confirmText}
          cancelText="Cancel"
          severity={actionConfirmDialog.severity}
          onCancel={handleCancelActionConfirm}
          onConfirm={handleConfirmAction}
        />
      )}
      <ConfirmDialog
        open={pendingFlagToggle != null}
        title={pendingFlagToggle ? 'Flag this truck?' : 'Remove flag from this truck?'}
        message={
          pendingFlagToggle
            ? 'This will mark the repair truck for attention.'
            : 'This will clear the attention flag from the repair truck.'
        }
        confirmText={pendingFlagToggle ? 'Flag truck' : 'Remove flag'}
        cancelText="Cancel"
        severity="warning"
        onCancel={() => setPendingFlagToggle(null)}
        onConfirm={async () => {
          if (pendingFlagToggle == null) return
          try {
            await onToggleFlag(repairTruck.id, pendingFlagToggle)
          } finally {
            setPendingFlagToggle(null)
          }
        }}
      />
      <TimelineDialog
        open={isTimelineDialogOpen}
        onClose={() => setIsTimelineDialogOpen(false)}
        vehicle={vehicle}
      />
      <InspectionChecklistAnswersDialog
        open={isChecklistDialogOpen}
        onClose={() => setIsChecklistDialogOpen(false)}
        repairTruck={repairTruck}
        completeChecklistId={completeChecklistId}
        intakeChecklistId={intakeChecklistId}
        initialTab="complete"
      />
      <MediaGalleryDialog
        open={Boolean(openMediaGroupEntries)}
        onClose={() => setOpenMediaGroupEntries(null)}
        photos={[...photoDialogItems, ...damagePhotoDialogItems]}
        videos={videoDialogItems}
      />
      <RepairEstimatePdfCompareDialog
        open={Boolean(estimatePdfCompare)}
        onClose={() => setEstimatePdfCompare(null)}
        older={estimatePdfCompare?.older ?? null}
        newer={estimatePdfCompare?.newer ?? null}
      />
    </Card>
  )
}

const RepairTrackingView: React.FC<RepairTrackingViewProps> = ({
  items,
  getRepairShopLabel,
  getUserName,
  onAddNote,
  onEditNote,
  onSetNoteVisibleToRepairUsers,
  onDeleteNote,
  onUpdateWorkStatus,
  onReviewEstimate,
  onUpdatePaid,
  onUpdateBeingPainted,
  onToggleFlag,
  isAddingNote,
  isEditingNote,
  isDeletingNote,
  isUpdatingWorkStatus,
  isReviewingEstimate,
  isUpdatingPaid,
  isUpdatingBeingPainted,
  isUpdatingFlag,
}) => {
  const { pickupLocations } = usePickupLocations()
  const { repairShopLocations } = useRepairShopLocations()
  const { repairUsers } = useRepairUsers()
  const repairAuthorUserIds = useMemo(
    () => new Set(repairUsers.map((r) => r.id)),
    [repairUsers]
  )

  const pickupLocationsById = useMemo(
    () => new Map((pickupLocations ?? []).map((location) => [location.id, location])),
    [pickupLocations]
  )

  const repairShopLocationsById = useMemo(
    () => new Map((repairShopLocations ?? []).map((location) => [location.id, location])),
    [repairShopLocations]
  )

  const getLatestShipmentPlan = (item: RepairTruckWithTruck) => {
    const plans = item.truckWithVehicle.shipmentPlans ?? []
    if (plans.length === 0) return null
    const sorted = [...plans].sort((a, b) => {
      const tA = a.created_at?.toMillis?.() ?? 0
      const tB = b.created_at?.toMillis?.() ?? 0
      return tB - tA
    })
    return sorted[0]
  }

  const getCustomerContact = (item: RepairTruckWithTruck): ContactInfo | null => {
    const latestPlan = getLatestShipmentPlan(item)
    const finalLocation = latestPlan?.locations?.[latestPlan.locations.length - 1]
    if (!finalLocation || finalLocation.location_type !== 'pickup') return null
    const pickupLocation = pickupLocationsById.get(finalLocation.location_id)
    if (!pickupLocation) return null

    return {
      name: pickupLocation.contact_name || 'Unknown contact',
      phone: pickupLocation.contact_phone || 'No phone',
      address: formatLocationAddress(pickupLocation),
    }
  }

  const getRepairShopContact = (item: RepairTruckWithTruck): ContactInfo | null => {
    const repairShop = repairShopLocationsById.get(item.repairTruck.repair_shop_location_id)
    if (!repairShop) return null

    return {
      name: repairShop.name || repairShop.contact_name || 'Unknown shop',
      phone: repairShop.contact_phone || 'No phone',
      address: formatLocationAddress(repairShop),
    }
  }

  /** Matches GroupsTab / RepairShopChecklistDialog: custom id or per-shop `repair-shop-{id}` doc */
  const getCompleteChecklistIdForItem = (item: RepairTruckWithTruck): string => {
    const shopId = item.repairTruck.repair_shop_location_id
    const shop = shopId ? repairShopLocationsById.get(shopId) : undefined
    return resolveCompleteChecklistId(shop ? { id: shopId, inspection_checklist_id: shop.inspection_checklist_id } : null)
  }

  const getIntakeChecklistIdForItem = (item: RepairTruckWithTruck): string => {
    const shopId = item.repairTruck.repair_shop_location_id
    const shop = shopId ? repairShopLocationsById.get(shopId) : undefined
    return resolveIntakeChecklistId(shop ? { id: shopId, intake_checklist_id: shop.intake_checklist_id } : null)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {items.map((item) => (
        <RepairTruckTrackingCard
          key={item.repairTruck.id}
          item={item}
          getRepairShopLabel={getRepairShopLabel}
          getUserName={getUserName}
          customerContact={getCustomerContact(item)}
          repairShopContact={getRepairShopContact(item)}
          onAddNote={onAddNote}
          onEditNote={onEditNote}
          onSetNoteVisibleToRepairUsers={onSetNoteVisibleToRepairUsers}
          onDeleteNote={onDeleteNote}
          onUpdateWorkStatus={onUpdateWorkStatus}
          onReviewEstimate={onReviewEstimate}
          onUpdatePaid={onUpdatePaid}
          onUpdateBeingPainted={onUpdateBeingPainted}
          onToggleFlag={onToggleFlag}
          isAddingNote={isAddingNote}
          isEditingNote={isEditingNote}
          isDeletingNote={isDeletingNote}
          isUpdatingWorkStatus={isUpdatingWorkStatus}
          isReviewingEstimate={isReviewingEstimate}
          isUpdatingPaid={isUpdatingPaid}
          isUpdatingBeingPainted={isUpdatingBeingPainted}
          isUpdatingFlag={isUpdatingFlag}
          repairAuthorUserIds={repairAuthorUserIds}
          completeChecklistId={getCompleteChecklistIdForItem(item)}
          intakeChecklistId={getIntakeChecklistIdForItem(item)}
        />
      ))}
    </Box>
  )
}

export default RepairTrackingView
